import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "@/lib/auth";
import { Toaster } from "sonner";

import Login from "@/pages/Login";
import AuthCallback from "@/pages/AuthCallback";
import Dashboard from "@/pages/Dashboard";
import Projects from "@/pages/Projects";
import ProjectDossier from "@/pages/ProjectDossier";
import DiscoveryLab from "@/pages/DiscoveryLab";
import PromptStudio from "@/pages/PromptStudio";
import Builds from "@/pages/Builds";
import Reviews from "@/pages/Reviews";
import Audits from "@/pages/Audits";
import Chat from "@/pages/Chat";
import Operations from "@/pages/Operations";
import Settings from "@/pages/Settings";
import CustomerPortal from "@/pages/CustomerPortal";
import CustomerProject from "@/pages/CustomerProject";

const ProtectedRoute = ({ children, allow = "any" }) => {
  const { user, loading, isInternal, isCustomer } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-neutral-50">
        <div className="font-mono text-xs text-neutral-500">PRÜFE SITZUNG …</div>
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  if (allow === "internal" && !isInternal()) return <Navigate to="/portal" replace />;
  if (allow === "customer" && !isCustomer() && !isInternal()) return <Navigate to="/login" replace />;
  return children;
};

const RootRedirect = () => {
  const { user, loading, isInternal } = useAuth();
  if (loading) return null;
  if (!user) return <Navigate to="/login" replace />;
  return <Navigate to={isInternal() ? "/dashboard" : "/portal"} replace />;
};

function AppRouter() {
  const location = useLocation();
  // Synchronous detection of OAuth callback to avoid race conditions
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/" element={<RootRedirect />} />
      <Route path="/login" element={<Login />} />

      {/* Internal */}
      <Route path="/dashboard" element={<ProtectedRoute allow="internal"><Dashboard /></ProtectedRoute>} />
      <Route path="/projects" element={<ProtectedRoute allow="internal"><Projects /></ProtectedRoute>} />
      <Route path="/projects/:projectId" element={<ProtectedRoute allow="internal"><ProjectDossier /></ProtectedRoute>} />
      <Route path="/discovery" element={<ProtectedRoute allow="internal"><DiscoveryLab /></ProtectedRoute>} />
      <Route path="/prompts" element={<ProtectedRoute allow="internal"><PromptStudio /></ProtectedRoute>} />
      <Route path="/builds" element={<ProtectedRoute allow="internal"><Builds /></ProtectedRoute>} />
      <Route path="/reviews" element={<ProtectedRoute allow="internal"><Reviews /></ProtectedRoute>} />
      <Route path="/chat" element={<ProtectedRoute allow="internal"><Chat /></ProtectedRoute>} />
      <Route path="/audits" element={<ProtectedRoute allow="internal"><Audits /></ProtectedRoute>} />
      <Route path="/operations" element={<ProtectedRoute allow="internal"><Operations /></ProtectedRoute>} />
      <Route path="/settings" element={<ProtectedRoute allow="internal"><Settings /></ProtectedRoute>} />

      {/* External / Customer */}
      <Route path="/portal" element={<ProtectedRoute allow="customer"><CustomerPortal /></ProtectedRoute>} />
      <Route path="/portal/:projectId" element={<ProtectedRoute allow="customer"><CustomerProject /></ProtectedRoute>} />

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <AppRouter />
          <Toaster position="bottom-right" />
        </BrowserRouter>
      </AuthProvider>
    </div>
  );
}

export default App;
